import keys from 'constants/keys'
import { Subtask, Task } from 'constants/interfaces'
import { extractSubstringAfterDelimiter } from 'utils'
import { isServiceManagedLink } from './link'

/** sales-ms-tasks-v2 CourseProgressStatus, derived by the server from what the SCORM course reports. */
export const COURSE_PROGRESS_STATUS = { IN_PROGRESS: 1, COMPLETED: 2, PASSED: 3, FAILED: 4 } as const

/** The learner's saved course progress (GET /course-progress). Only the fields the panel shows. */
export interface ICourseProgress {
  status: number
  score?: number | null
  launch_count?: number | null
  result_reported_at_ts?: number | null
  result_error?: string | null
  updated_at_ts?: number | null
}

/** The task fields the server resolves the progress row from — the same ones the course link carries. */
export interface ICourseProgressRequest {
  tid: string
  st_id: string
  context_id: string
  task_context_id: string
  recipient_type: number
  is_individual_results: boolean
  recurrence_ts: string | null
}

export type CourseState = 'notStarted' | 'inProgress' | 'completed' | 'passed' | 'failed'

/**
 * A SCORM course: a service-managed link on the courses host ("external"). Storee forms are also
 * service-managed but have no course progress, so they never trigger the request.
 */
export const isCourseLink = (subtask: Pick<Subtask, 'external_link' | 'is_external_status_update'>) =>
  isServiceManagedLink(subtask) &&
  subtask.external_link!.includes(keys.EXTERNAL_LINK_PHRASE) &&
  !subtask.external_link!.includes(keys.FORMS_STOREE)

/** Mirrors the course link's parameters (model/link getFormattedLink), so both reach the same progress row. */
export const courseProgressRequest = (
  subtask: Pick<Subtask, 'tid' | 'st_id'>,
  task: Pick<Task, 'retail_id_context_id' | 'recipient_type' | 'is_individual_results' | 'recurrence_ts'>,
  contextId: string,
  rid: string
): ICourseProgressRequest => ({
  tid: subtask.tid,
  st_id: subtask.st_id,
  context_id: contextId,
  task_context_id: extractSubstringAfterDelimiter(task.retail_id_context_id, rid),
  recipient_type: task.recipient_type,
  is_individual_results: !!task.is_individual_results,
  recurrence_ts: task.recurrence_ts ?? null,
})

const STATUS_TO_STATE: Record<number, CourseState> = {
  [COURSE_PROGRESS_STATUS.IN_PROGRESS]: 'inProgress',
  [COURSE_PROGRESS_STATUS.COMPLETED]: 'completed',
  [COURSE_PROGRESS_STATUS.PASSED]: 'passed',
  [COURSE_PROGRESS_STATUS.FAILED]: 'failed',
}

/** No saved progress means the learner never opened the course. */
export const courseState = (progress: ICourseProgress | null): CourseState =>
  (progress && STATUS_TO_STATE[progress.status]) || 'notStarted'

export const isCourseFinished = (state: CourseState) => state === 'completed' || state === 'passed'
